<template>
  <div>

    <hr v-if="blockIndex > 0">

    <c-introduction
      v-if="config.meta.introduction && blockIndex === 0"
      v-html="config.meta.introduction"
    />

    <c-profile-display v-if="config.meta.show_profile" />

    <c-form-fieldset
      v-for="groupConfig in config.groups"
      :key="groupConfig.name"
      :legend="groupConfig.label"
      :class="groupConfig.marginTop ? 'form-fieldset__padded' : ''"
      :detail="saveStatus === $config.status.SAVING ? 'Wird gespeichert…' : null"
    >

      <c-grid>
        <c-grid-item
          v-if="groupConfig.introduction"
          :span="12"
          v-html="groupConfig.introduction"
        />

        <c-grid-item
          v-for="(fieldConfig, f_index) in groupConfig.fields"
          v-if="fieldConfig.id"
          :key="f_index"
          :span="parseInt(fieldConfig.units)"
        >

          <c-form-checkbox
            v-if="fieldConfig.type === 'checkbox'"
            :id="`${fieldConfig.id}-${blockIndex}`"
            :key="f_index"
            :label="getFieldLabel(fieldConfig)"
            :help="fieldConfig.help"
            v-model="values[groupConfig.name][fieldConfig.id]"
            :has-error="fieldHasError(groupConfig.name, fieldConfig.id)"
            @change="updateField(groupConfig.name, fieldConfig.id)"
          />

          <c-form-datepicker
            v-else-if="fieldConfig.type === 'date'"
            :id="`${fieldConfig.id}-${blockIndex}`"
            :label="getFieldLabel(fieldConfig)"
            :label-after="fieldConfig.label_after"
            :help="fieldConfig.help"
            :min-date="(fieldConfig.options && fieldConfig.options.hasOwnProperty('earliest')) ? fieldConfig.options.earliest : ''"
            :max-date="(fieldConfig.options && fieldConfig.options.hasOwnProperty('latest')) ? fieldConfig.options.latest : ''"
            v-model="values[groupConfig.name][fieldConfig.id]"
            :has-error="fieldHasError(groupConfig.name, fieldConfig.id)"
            :is-valid="fieldIsValid(groupConfig.name, fieldConfig.id)"
            @input="updateField(groupConfig.name, fieldConfig.id)"
          />

          <c-form-input
            v-else-if="fieldConfig.type === 'input'"
            :id="`${fieldConfig.id}-${blockIndex}`"
            :label="getFieldLabel(fieldConfig)"
            :label-after="fieldConfig.label_after"
            :help="fieldConfig.help"
            :type="fieldConfig.options && fieldConfig.options.type"
            v-model="values[groupConfig.name][fieldConfig.id]"
            :has-error="fieldHasError(groupConfig.name, fieldConfig.id)"
            :is-valid="fieldIsValid(groupConfig.name, fieldConfig.id)"
            :max-length="fieldGetMaxLength(fieldConfig.validators)"
            :validation-hints="getFieldValidationHints(fieldConfig.validators)"
            :pattern="getFieldPattern(fieldConfig.validators)"
            @input="updateField(groupConfig.name, fieldConfig.id)"
          />

          <c-form-select
            v-else-if="fieldConfig.type === 'select'"
            :id="`${fieldConfig.id}-${blockIndex}`"
            :label="getFieldLabel(fieldConfig)"
            :label-after="fieldConfig.label_after"
            :help="fieldConfig.help"
            v-model="values[groupConfig.name][fieldConfig.id]"
            :has-error="fieldHasError(groupConfig.name, fieldConfig.id)"
            :is-valid="fieldIsValid(groupConfig.name, fieldConfig.id)"
            @input="updateField(groupConfig.name, fieldConfig.id)"
          >

            <option
              v-if="fieldConfig.options.default"
              :value="fieldConfig.options.default.value"
              :selected="values[groupConfig.name][fieldConfig.id] === ''"
              disabled
            >
              {{ fieldConfig.options.default.label }}
            </option>

            <option
              v-for="option in fieldConfig.options.options"
              :key="option.value"
              :value="option.value"
              :selected="values[groupConfig.name][fieldConfig.id] === option.value"
            >
              {{ option.label }}
            </option>

          </c-form-select>

          <c-form-list
            v-else-if="fieldConfig.type === 'radio'"
            :id="`${fieldConfig.id}-${blockIndex}`"
            :label="getFieldLabel(fieldConfig)"
            :help="fieldConfig.help"
            :has-error="fieldHasError(groupConfig.name, fieldConfig.id)"
            :is-valid="fieldIsValid(groupConfig.name, fieldConfig.id)"
          >

            <c-form-list-item
              v-for="option in fieldConfig.options.options"
              :key="option.value"
            >
              <c-form-radio
                :id="`${fieldConfig.id}-${blockIndex}-${option.value}`"
                v-model="values[groupConfig.name][fieldConfig.id]"
                :label="option.label"
                :value="option.value"
                :name="fieldConfig.id"
                @change="updateField(groupConfig.name, fieldConfig.id)"
              />
            </c-form-list-item>

          </c-form-list>

          <c-form-textarea
            v-else-if="fieldConfig.type === 'textarea'"
            :id="`${fieldConfig.id}-${blockIndex}`"
            :label="getFieldLabel(fieldConfig)"
            :label-after="fieldConfig.label_after"
            :help="fieldConfig.help"
            :rows="fieldConfig.options && fieldConfig.options.rows"
            v-model="values[groupConfig.name][fieldConfig.id]"
            :has-error="fieldHasError(groupConfig.name, fieldConfig.id)"
            :is-valid="fieldIsValid(groupConfig.name, fieldConfig.id)"
            :max-length="fieldGetMaxLength(fieldConfig.validators)"
            @input="updateField(groupConfig.name, fieldConfig.id)"
          />

          <c-form-upload
            v-else-if="fieldConfig.type === 'upload'"
            :id="`${fieldConfig.id}-${blockIndex}`"
            :label="getFieldLabel(fieldConfig)"
            :label-after="fieldConfig.label_after"
            :help="fieldConfig.help"
            v-model="values[groupConfig.name][fieldConfig.id]"
            :extensions="getOption(fieldConfig, 'filetypes', null)"
            :size="parseInt(getOption(fieldConfig, 'max_size', 0))"
            :maximum="parseInt(getOption(fieldConfig, 'max_files', 1))"
            :upload-uri="getUploadUri(groupConfig.name, fieldConfig.id)"
            :delete-uri="getDeleteUri(groupConfig.name, fieldConfig.id)"
            :has-error="fieldHasError(groupConfig.name, fieldConfig.id)"
            :is-valid="fieldIsValid(groupConfig.name, fieldConfig.id)"
            @complete="updateUploadField(groupConfig.name, fieldConfig.id, $event)"
            @deleted="updateUploadField(groupConfig.name, fieldConfig.id, $event)"
          />

        </c-grid-item>

      </c-grid>
    </c-form-fieldset>

  </div>
</template>

<script>
import CFormCheckbox from '@/components/form/CFormCheckbox'
import CFormDatepicker from '@/components/form/CFormDatepicker'
import CFormFieldset from '@/components/form/CFormFieldset'
import CFormInput from '@/components/form/CFormInput'
import CFormSelect from '@/components/form/CFormSelect'
import CFormList from '@/components/form/CFormList'
import CFormListItem from '@/components/form/CFormListItem'
import CFormRadio from '@/components/form/CFormRadio'
import CFormTextarea from '@/components/form/CFormTextarea'
import CFormUpload from '@/components/form/CFormUpload'
import CGrid from '@/components/CGrid'
import CGridItem from '@/components/CGridItem'
import CIntroduction from '@/components/CIntroduction'
import CProfileDisplay from '@/components/CProfileDisplay'

import config from '@/config'
import eventBus from '@/functions/eventBus'
import handleRequestError from '@/functions/handleRequestError'

export default {
  name: 'CProcessBlock',

  components: {
    CFormCheckbox,
    CFormDatepicker,
    CFormFieldset,
    CFormInput,
    CFormSelect,
    CFormList,
    CFormListItem,
    CFormRadio,
    CFormTextarea,
    CFormUpload,
    CGrid,
    CGridItem,
    CIntroduction,
    CProfileDisplay
  },

  props: {
    name: {
      type: String,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    blockIndex: {
      type: Number,
      required: true
    },
    validations: {
      type: Object
    },
    validator: {
      type: Object,
      required: true
    },
    processId: {
      type: String,
      required: true
    },
    workflow: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      values: {},
      saveStatus: this.$config.status.IDLE,
      saveTimeout: null,
      saveInterval: 1000
    }
  },

  computed: {
    blockId () {
      return 'bi_' + this.blockIndex
    }
  },

  created () {
    this.$store.dispatch('data/addBlock', {
      workflow: this.workflow,
      blockName: this.name,
      blockIndex: this.blockId
    })

    for (let gi = 0; gi < this.config.groups.length; gi++) {
      let groupConfig = this.config.groups[gi]

      this.$store.dispatch('data/addGroup', {
        workflow: this.workflow,
        blockName: this.name,
        blockIndex: this.blockId,
        group: groupConfig.name
      })

      this.$set(this.values, groupConfig.name, {})

      for (let fi = 0; fi < groupConfig.fields.length; fi++) {
        let fieldConfig = groupConfig.fields[fi]
        if (fieldConfig.id) {
          let storageValue = this.$store.state.data.blocks[this.workflow][this.name][this.blockId][groupConfig.name][fieldConfig.id]
          let defaultValue = fieldConfig.type === 'checkbox' ? false : ''
          if (fieldConfig.hasOwnProperty('default')) {
            defaultValue = fieldConfig.default
          }
          storageValue = typeof storageValue === 'undefined' ? defaultValue : storageValue
          this.$set(this.values[groupConfig.name], fieldConfig.id, storageValue)
          if (storageValue !== '' && storageValue !== null) {
            this.validator.touch(this.name, this.blockId, groupConfig.name, fieldConfig.id)
          }
        }
      }
    }

    this.$api.post('workflow/process/' + this.processId + '/store_block/', {
      block_name: this.name,
      block_index: this.blockId,
      block_data: this.values
    }).then(response => {
      eventBus.$emit('blockSaved', this.blockId, true)
    }).catch({
      // Do nothing
    })
  },

  methods: {
    getFieldLabel (fieldConfig, defaultValue) {
      defaultValue = typeof (defaultValue) === 'undefined' ? '' : defaultValue
      let value = fieldConfig['label'] ? fieldConfig['label'] : defaultValue
      if (fieldConfig.hasOwnProperty('validators')) {
        for (let key in fieldConfig['validators']) {
          if (fieldConfig['validators'].hasOwnProperty(key) &&
            fieldConfig['validators'][key]['type'] === 'required') {
            value += ' *'
            break
          }
        }
      }

      return value
    },

    getUploadUri (groupName, fieldId) {
      return this.getUploadApiUri('upload', groupName, fieldId)
    },

    getDeleteUri (groupName, fieldId) {
      return this.getUploadApiUri('delete', groupName, fieldId)
    },

    getUploadApiUri (type, groupName, fieldId) {
      let uri = config.appUrl + '/api/workflow/upload/' + this.processId + '/' + type + '/'
      uri += '?bn=' + this.name
      uri += '&bi=' + this.blockId
      uri += '&gn=' + groupName
      uri += '&fid=' + fieldId
      return uri
    },

    getOption (config, option, fallback) {
      if (!config.hasOwnProperty('options')) {
        return fallback
      }
      return config.options.hasOwnProperty(option) ? config.options[option] : fallback
    },

    updateField (groupName, fieldId) {
      // Touch field to mark it as dirty
      this.validator.touch(this.name, this.blockId, groupName, fieldId)

      // Store data to local storage
      this.$store.dispatch('data/updateField', {
        workflow: this.workflow,
        blockName: this.name,
        blockIndex: this.blockId,
        group: groupName,
        field: fieldId,
        value: this.values[groupName][fieldId]
      })

      // Tell form renderer to validate the block
      eventBus.$emit('validateBlock')

      // Save to server after n milliseconds of inactivity
      clearTimeout(this.saveTimeout)
      this.saveTimeout = setTimeout(() => {
        this.storeDataToServer(groupName, fieldId)
      }, this.saveInterval)
    },

    updateUploadField (groupName, fieldId, fileName) {
      this.values[groupName][fieldId] = fileName

      // Store data to local storage
      this.$store.dispatch('data/updateField', {
        workflow: this.workflow,
        blockName: this.name,
        blockIndex: this.blockId,
        group: groupName,
        field: fieldId,
        value: this.values[groupName][fieldId]
      })

      // Tell form renderer to validate the block
      eventBus.$emit('validateBlock')

      this.storeDataToServer(groupName, fieldId)
    },

    fieldGetMaxLength (validators) {
      if (!Array.isArray(validators)) return 0

      for (const v of validators) {
        if (v['type'] === 'max_length') {
          return v['value']
        }
      }

      return 0
    },

    getFieldPattern (validators) {
      if (!Array.isArray(validators)) return

      for (const v of validators) {
        if (v['type'] === 'telephone') {
          return /\+[0-9]+$/
        }
      }
    },

    getFieldValidationHints (validators) {
      let hints = []

      if (!Array.isArray(validators)) return

      for (const v of validators) {
        if (v['type'] === 'telephone') {
          hints.push('Format: +49123456789')
        }
      }
      return hints
    },

    fieldHasError (groupName, fieldName) {
      let blockIndex = this.blockId
      return (
        this.validator.isDirty(this.name, blockIndex, groupName, fieldName) &&
        this.validator.hasError(this.name, blockIndex, groupName, fieldName)
      )
    },

    fieldIsValid (groupName, fieldName) {
      let blockIndex = this.blockId
      return (
        this.validator.isDirty(this.name, blockIndex, groupName, fieldName) &&
        !this.validator.hasError(this.name, blockIndex, groupName, fieldName)
      )
    },

    storeDataToServer () {
      this.saveStatus = this.$config.status.SAVING

      this.$api.post('workflow/process/' + this.processId + '/store_block/', {
        block_name: this.name,
        block_index: this.blockId,
        block_data: this.values
      })
        .then(response => {
          this.saveStatus = this.$config.status.SUCCESS
        })
        .catch(error => {
          handleRequestError({
            error,
            fallback: 'Ihre Förderprogramme konnten nicht automatisch gespeichert werden.'
          })

          this.saveStatus = this.$config.status.ERROR
        })
    }
  }
}

</script>
